import { useEffect, useMemo, useState } from "react";

import { Check, Search, X } from "lucide-react";

export default function AccessItemPicker({
  open,
  title,
  items = [],
  itemIdKey,
  selectedIds = [],
  disabled = false,
  onChange,
  onClose,
  maxSelection = 100,
}) {
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState([]);

  useEffect(() => {
    if (!open) return;

    setSearch("");
    setDraft(selectedIds.map(Number));
  }, [open, selectedIds]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return items.filter((item) => {
      if (!term) return true;

      return `
        ${item.title || ""}
        ${item.streamType || ""}
        ${item.visibility || ""}
      `
        .toLowerCase()
        .includes(term);
    });
  }, [items, search]);

  if (!open) return null;

  const toggle = (value) => {
    const id = Number(value);

    setDraft((previous) => {
      if (previous.includes(id)) {
        return previous.filter((item) => item !== id);
      }

      if (previous.length >= maxSelection) {
        return previous;
      }

      return [...previous, id];
    });
  };

  return (
    <div
      className="cfa-picker-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        className="cfa-picker"
        role="dialog"
        aria-modal="true"
        aria-label={`Choose ${title}`}
      >
        <div className="cfa-picker__header">
          <div>
            <h2>Choose {title}</h2>

            <p>
              {draft.length} of {maxSelection} selected
            </p>
          </div>

          <button
            type="button"
            className="cfa-icon"
            onClick={onClose}
            aria-label="Close selection"
          >
            <X size={18} />
          </button>
        </div>

        <div className="cfa-search">
          <Search size={17} />

          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={`Search ${title.toLowerCase()}`}
            aria-label={`Search ${title}`}
          />
        </div>

        <div className="cfa-picker__list">
          {filtered.length === 0 ? (
            <p className="cfa-empty">No matching items.</p>
          ) : (
            filtered.map((item) => {
              const id = Number(item[itemIdKey]);

              const checked = draft.includes(id);

              return (
                <label key={id} className="cfa-picker__option">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={
                      disabled || (!checked && draft.length >= maxSelection)
                    }
                    onChange={() => toggle(id)}
                  />

                  <span>
                    <strong>{item.title || `Item #${id}`}</strong>

                    <small>
                      {item.streamType || item.visibility || item.status || ""}
                    </small>
                  </span>

                  {checked && <Check size={16} />}
                </label>
              );
            })
          )}
        </div>

        <div className="cfa-picker__actions">
          <button
            type="button"
            className="cfa-btn cfa-btn--muted"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            type="button"
            className="cfa-btn cfa-btn--primary"
            disabled={disabled}
            onClick={() => {
              onChange(draft);
              onClose();
            }}
          >
            Use selected ({draft.length})
          </button>
        </div>
      </section>
    </div>
  );
}
